const sourceName = value => String(value || '').replace(/^\d{3}_/, '').toLowerCase();

// Preserve offsets, including documents containing literal JSON newline escapes.
export function referenceRanges(text = '') {
  const starts = [...text.matchAll(/(?:^|\n|\\n)\s*(?:#{1,6}\s*)?(?:\d+[. ]+)?(?:References|Bibliography)\b|\bReferences\s*\[?1[.\]]/gi)];
  // Some PDF conversions omit the heading and begin midway through a list.
  // Require three consecutive numbered author entries with citation years.
  const entries = [...text.matchAll(/(?:^|\s|\\n)(?:\[(\d+)\]|(\d+)\.?)\s+(?=[A-Z][A-Za-z'-]+\s+[A-Z][A-Za-z.-]{0,7}\s*[,.;])/g)];
  for (let i = 0; i + 2 < entries.length; i += 1) {
    const run = entries.slice(i, i + 3);
    const firstNumber = Number(run[0][1] || run[0][2]);
    if (run.every((entry, j) => Number(entry[1] || entry[2]) === firstNumber + j
      && (j === 0 || entry.index - run[j - 1].index < 2500)
      && /(?:19|20)\d{2}\s*[;:)]/.test(text.slice(entry.index, entries[i + j + 1]?.index || entry.index + 2500)))) {
      if (!starts.some(start => start.index <= run[0].index)) starts.push(run[0]);
      break;
    }
  }
  return starts.map(match => {
    const after = match.index + match[0].length;
    const tail = text.slice(after);
    // Converted tables can follow the bibliography; they are not references.
    const end = /(?:\n|\\n)\s*(?:#{1,6}\s*)?(?:Appendix|Supplementary|Acknowledg(?:e)?ments|Funding|Author contributions|(?:Table|Figure)\s+\d)\b|(?:\n|\\n)\s*\{\s*(?:\n|\\n)?\s*"records"/i.exec(tail);
    return [match.index, end ? after + end.index : text.length];
  });
}

const inReference = (position, ranges) => ranges.some(([start, end]) => position >= start && position < end);
const parseArray = value => {
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value || '[]'); } catch { return []; }
};

export function referenceClassifier(documents, textUnits) {
  const byId = new Map();
  const byName = new Map();
  documents.forEach(document => {
    const text = document.text || '';
    const source = { text, searchText: text.replace(/\\[nr]/g, '  '), ranges: referenceRanges(text) };
    byId.set(document.id, source);
    byName.set(sourceName(document.title), source);
  });
  const units = new Map(textUnits.map(unit => [unit.id, unit]));
  return entity => {
    const mentions = parseArray(entity.source_mentions);
    if (mentions.length) {
      return mentions.every(mention => {
        const source = byName.get(sourceName(mention.source));
        if (source?.ranges.length && Number.isFinite(Number(mention.evidence_start))) {
          return inReference(Number(mention.evidence_start), source.ranges);
        }
        const scopes = parseArray(mention.mention_scopes);
        return scopes.length > 0 && scopes.every(scope => scope === 'reference');
      });
    }
    // Legacy v1: only hide when the full source text establishes every exact
    // name occurrence is in a bibliography. Mixed chunks or unknowns stay visible.
    const sourceIds = new Set();
    for (const id of entity.text_unit_ids || []) {
      const unit = units.get(id);
      if (!unit) return false;
      const ids = unit.document_ids || (unit.document_id ? [unit.document_id] : []);
      if (!ids.length) return false;
      ids.forEach(sourceId => sourceIds.add(sourceId));
    }
    if (!sourceIds.size || !entity.title) return false;
    const escaped = entity.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s*');
    const name = new RegExp(`(^|[^\\p{L}\\p{N}])(${escaped})(?=$|[^\\p{L}\\p{N}])`, 'giu');
    return [...sourceIds].every(id => {
      const source = byId.get(id);
      if (!source?.ranges.length) return false;
      const matches = [...source.searchText.matchAll(name)];
      return matches.length > 0 && matches.every(match => inReference(match.index + match[1].length, source.ranges));
    });
  };
}
